import _ from 'lodash';
import {
  createFetchPaginatedActionForNamespace,
  createReducer,
  createFetchSelectors,
  createFetchReducersWithPagination,
  createFetchActionForNamespace,
  createFetchReducers,
  noDataForAction,
  createPostActionForNamespace
} from 'utils/reducers';

import {selectFilters, FILTERS} from 'ducks/filters';
import {transformFiltersForStoreToFiltersForApi} from 'utils/filters';
import {selectSort, SORTING} from 'ducks/sorting';

import {attachUuidKey} from 'utils/common';
import {PAGINATION, selectPagination} from 'ducks/pagination';
import {transformPaginationForStoreToPaginationForApi} from 'utils/pagination';
import {transformSortForStoreToSortForUrl} from 'utils/sort';

const createFetchAction = createFetchActionForNamespace('bookingSets');
const createFetchActionWithPagination = createFetchPaginatedActionForNamespace(
  'bookingSets'
);
const createPostAction = createPostActionForNamespace('bookingSets');

export const fetchBookingSets = createFetchActionWithPagination({
  name: 'FETCH_BOOKING_SETS',
  initialAction: ({locationRef}, extraActions = []) => ({
    payload: {locationRef, extraActions}
  }),
  stateKey: 'bookingSets',
  defaultStateValue: [],
  extraGetParamsSelectors: [
    state =>
      transformFiltersForStoreToFiltersForApi(
        selectFilters(state, FILTERS.BOOKING_SET_LIST)
      ),
    state =>
      transformPaginationForStoreToPaginationForApi(
        selectPagination(state, PAGINATION.BOOKING_SET_LIST)
      ),
    state =>
      transformSortForStoreToSortForUrl(
        selectSort(state, SORTING.BOOKING_SET_LIST)
      )
  ],
  paginationNamespace: PAGINATION.BOOKING_SET_LIST
});
export const selectBookingSets = createFetchSelectors(fetchBookingSets).data;
export const selectBookingSetsAreLoading = createFetchSelectors(
  fetchBookingSets
).isLoading;

export const fetchBookingSetsByTenant = createFetchAction({
  name: 'FETCH_BOOKING_SETS_BY_TENANT',
  initialAction: ({locationRef, tenantId}, extraActions) => ({
    payload: {
      locationRef,
      tenantId,
      extraActions
    }
  }),
  stateKey: 'bookingSetsByTenant',
  defaultStateValue: []
});
export const selectBookingSetsByTenant = createFetchSelectors(
  fetchBookingSetsByTenant
).data;

// TODO: move this to ducks/bookingSets/detail
export const fetchBookingSet = createFetchAction({
  name: 'FETCH_BOOKING_SET',
  initialAction: ({bookingSetId}, extraActions = []) => ({
    payload: {
      bookingSetId,
      extraActions
    }
  }),
  stateKey: 'bookingSet'
});

export const selectBookingSet = createFetchSelectors(fetchBookingSet).data;
export const selectBookingSetIsLoading = createFetchSelectors(fetchBookingSet)
  .isLoading;

export const fetchBookingSetsFinanceData = createFetchAction({
  name: 'FETCH_BOOKING_SET_FINANCE',
  initialAction: ({bookingSetId}, extraActions = []) => ({
    payload: {
      bookingSetId,
      extraActions
    }
  }),
  stateKey: 'bookingSetFinanceData'
});

const selectBookingSetsFinanceDataSelector = createFetchSelectors(
  fetchBookingSetsFinanceData
);

const selectBookingSetsFinanceData = selectBookingSetsFinanceDataSelector.data;
export const selectBookingSetsFinanceItems = state =>
  selectBookingSetsFinanceData(state).items;
export const selectBookingSetsFinanceTotals = state =>
  selectBookingSetsFinanceData(state).totals;

export const selectBookingSetsFinanceDataIsLoading =
  selectBookingSetsFinanceDataSelector.isLoading;

export const fetchBookingSetDepositFinanceData = createFetchAction({
  name: 'FETCH_BOOKING_SET_DEPOSIT_FINANCE',
  initialAction: ({bookingSetId}, extraActions = []) => ({
    payload: {
      bookingSetId,
      extraActions
    }
  }),
  stateKey: 'bookingSetDepositFinanceData'
});

const selectBookingSetDepositFinanceDataSelector = createFetchSelectors(
  fetchBookingSetDepositFinanceData
);

const selectBookingSetDepositFinanceData =
  selectBookingSetDepositFinanceDataSelector.data;
export const selectBookingSetDepositFinanceItems = state =>
  selectBookingSetDepositFinanceData(state).items;
export const selectBookingSetDepositFinanceTotals = state =>
  selectBookingSetDepositFinanceData(state).totals;

export const selectBookingSetDepositFinanceDataIsLoading =
  selectBookingSetDepositFinanceDataSelector.isLoading;

export const fetchVoidableInvoicesForBookingSet = createFetchAction({
  name: 'FETCH_VOIDABLE_INVOICES_FOR_BOOKING_SET',
  initialAction: ({bookingSetId}, extraActions = []) => ({
    payload: {
      bookingSetId,
      extraActions
    }
  }),
  stateKey: 'voidableInvoices'
});

export const fetchVoidableInvoicesForBookingSetSelector = createFetchSelectors(
  fetchVoidableInvoicesForBookingSet
);
export const selectVoidableInvoicesForBookingSetIsLoading =
  fetchVoidableInvoicesForBookingSetSelector.isLoading;

export const selectVoidableInvoicesForBookingSet =
  fetchVoidableInvoicesForBookingSetSelector.data;

export const fetchArrivalRoomChecks = createFetchAction({
  name: 'FETCH_ARRIVALS_ROOM_CHECKS',
  initialAction: ({bookingSetId}, extraActions = []) => ({
    payload: {
      bookingSetId,
      extraActions
    }
  }),
  stateKey: 'arrivalRoomChecks',
  defaultStateValue: []
});

export const fetchArrivalRoomChecksSelector = createFetchSelectors(
  fetchArrivalRoomChecks
);

export const selectArrivalRoomChecksAreLoading =
  fetchArrivalRoomChecksSelector.isLoading;

export const selectArrivalRoomChecksForBookingSet =
  fetchArrivalRoomChecksSelector.data;

export const fetchDepartureRoomChecks = createFetchAction({
  name: 'FETCH_DEPARTURE_ROOM_CHECKS',
  initialAction: ({bookingSetId}, extraAction = []) => ({
    payload: {
      bookingSetId,
      extraAction
    }
  }),
  stateKey: 'departureRoomChecks'
});

export const fetchDepartureRoomChecksSelector = createFetchSelectors(
  fetchDepartureRoomChecks
);

export const selectDepartureRoomChecksAreLoading =
  fetchDepartureRoomChecksSelector.isLoading;

export const selectDepartureRoomChecksForBookingSet =
  fetchDepartureRoomChecksSelector.data;

export const fetchDepositSummary = createFetchAction({
  name: 'FETCH_DEPOSIT_SUMMARY',
  initialAction: ({bookingSetId}, extraAction = []) => ({
    payload: {
      bookingSetId,
      extraAction
    }
  }),
  stateKey: 'depositSummary'
});

export const fetchDepositSummarySelector = createFetchSelectors(
  fetchDepositSummary
);

export const selectDepositSummary = fetchDepositSummarySelector.data;

export const fetchRoomChecksTimeline = createFetchAction({
  name: 'FETCH_ROOM_CHECKS_TIMELINE',
  initialAction: ({bookingSetId}, extraAction = []) => ({
    payload: {bookingSetId, extraAction}
  }),
  stateKey: 'roomChecksTimeline'
});

export const fetchRoomChecksTimelineSelector = createFetchSelectors(
  fetchRoomChecksTimeline
);

export const selectRoomChecksTimeline = fetchRoomChecksTimelineSelector.data;

export const fetchRoomOptionsForAddArrivalRoomCheck = createFetchAction({
  name: 'FETCH_ROOM_OPTIONS_FOR_ADD_ARRIVAL_ROOM_CHECK',
  initialAction: ({bookingSetId}, extraActions = []) => ({
    payload: {
      bookingSetId,
      extraActions
    }
  })
});

export const fetchRoomOptionsForAddDepartureRoomCheck = createFetchAction({
  name: 'FETCH_ROOM_OPTIONS_FOR_ADD_DEPARTURE_ROOM_CHECK',
  initialAction: ({bookingSetId}, extraActions = []) => ({
    payload: {
      bookingSetId,
      extraActions
    }
  })
});

export const fetchImageUrlAndFileName = createFetchAction({
  name: 'FETCH_IMAGE_URL_AND_FILE_NAME',
  initialAction: ({imageIdentifier}, extraActions = []) => ({
    payload: {
      imageIdentifier,
      extraActions
    }
  }),
  stateKey: 'imageUrlAndFileName'
});

export const fetchDocumentFileName = createFetchAction({
  name: 'FETCH_DOCUMENT_FILE_NAME',
  initialAction: ({documentIdentifier}, extraActions = []) => ({
    payload: {
      documentIdentifier,
      extraActions
    }
  }),
  stateKey: 'documentFileName'
});

export const fetchDepositTypes = createFetchAction({
  name: 'FETCH_DEPOSIT_TYPES',
  initialAction: noDataForAction,
  stateKey: 'depositTypes',
  defaultStateValue: []
});
export const selectDepositTypes = createFetchSelectors(fetchDepositTypes).data;

export const fetchDepositSummaryStatuses = createFetchAction({
  name: 'FETCH_DEPOSIT_SUMMARY_STATUSES',
  initialAction: noDataForAction,
  stateKey: 'depositSummaryStatuses',
  defaultStateValue: []
});

export const selectDepositSummaryStatuses = createFetchSelectors(
  fetchDepositSummaryStatuses
).data;

export const createArrivalRoomCheck = createPostAction({
  name: 'CREATE_ARRIVAL_ROOM_CHECK',
  initialAction: (
    {
      bookingSetId,
      booking,
      existingDamages,
      description,
      imageIdentifiers,
      inventoryIdentifier
    },
    extraActions = []
  ) => ({
    payload: {
      bookingSetId,
      booking,
      existingDamages,
      description,
      imageIdentifiers,
      inventoryIdentifier,
      extraActions
    }
  }),
  formName: 'addArrivalRoomCheck'
});

export const createDepartureRoomCheck = createPostAction({
  name: 'CREATE_DEPARTURE_ROOM_CHECK',
  initialAction: (
    {
      bookingSetId,
      booking,
      totalDamageCharge,
      roomCheckDescription,
      imageIdentifiers,
      inventoryIdentifier,
      maintenanceIdentifier,
      paymentTakenAtFrontDesk,
      chargeFromDeposit,
      releaseFullDeposit,
      label,
      description,
      oneOffChargeReasonId,
      amount,
      createPayment,
      paymentMethod,
      paymentDate,
      invoiceDueDate,
      reference,
      xeroAccount,
      batchNumber,
      creditCardNumber
    },
    extraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {
      bookingSetId,
      booking,
      totalDamageCharge,
      roomCheckDescription,
      imageIdentifiers,
      inventoryIdentifier,
      maintenanceIdentifier,
      paymentTakenAtFrontDesk,
      chargeFromDeposit,
      releaseFullDeposit,
      label,
      description,
      oneOffChargeReasonId,
      amount,
      createPayment,
      paymentMethod,
      paymentDate,
      invoiceDueDate,
      reference,
      xeroAccount,
      batchNumber,
      creditCardNumber,
      extraActions,
      failureExtraActions
    }
  }),
  formName: 'addDepartureRoomCheckForm'
});

export const fetchArrivalRoomCheck = createFetchAction({
  name: 'FETCH_ARRIVAL_ROOM_CHECK',
  initialAction: ({roomCheckId, bookingSetId}, extraActions = []) => ({
    payload: {
      roomCheckId,
      bookingSetId,
      extraActions
    }
  })
});

export const fetchDepartureRoomCheck = createFetchAction({
  name: 'FETCH_DEPARTURE_ROOM_CHECK',
  initialAction: ({roomCheckId, bookingSetId}, extraActions = []) => ({
    payload: {
      roomCheckId,
      bookingSetId,
      extraActions
    }
  })
});

export const verifyArrivalRoomCheckImageUpload = createPostAction({
  name: 'VERIFY_ARRIVAL_ROOM_CHECK_IMAGE_UPLOAD',
  initialAction: (
    {bookingSetId, imageIdentifier, arrivalRoomCheckId},
    extraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {
      bookingSetId,
      imageIdentifier,
      arrivalRoomCheckId,
      extraActions,
      failureExtraActions
    }
  })
});

export const verifyArrivalRoomCheckInventoryUpload = createPostAction({
  name: 'VERIFY_ARRIVAL_ROOM_CHECK_INVENTORY_UPLOAD',
  initialAction: (
    {bookingSetId, documentIdentifier, arrivalRoomCheckId},
    extraActions = []
  ) => ({
    payload: {
      bookingSetId,
      documentIdentifier,
      arrivalRoomCheckId,
      extraActions
    }
  })
});

export const removeArrivalRoomCheckImage = createPostAction({
  name: 'REMOVE_ARRIVAL_ROOM_CHECK_IMAGE',
  initialAction: (
    {bookingSetId, arrivalRoomCheckId, roomCheckImageId},
    extraActions = []
  ) => ({
    payload: {
      bookingSetId,
      arrivalRoomCheckId,
      roomCheckImageId,
      extraActions
    }
  })
});

export const removeArrivalRoomCheckInventory = createPostAction({
  name: 'REMOVE_ARRIVAL_ROOM_CHECK_INVENTORY',
  initialAction: (
    {bookingSetId, arrivalRoomCheckId, roomCheckInventoryId},
    extraActions = []
  ) => ({
    payload: {
      bookingSetId,
      arrivalRoomCheckId,
      roomCheckInventoryId,
      extraActions
    }
  })
});

export const editArrivalRoomCheckExistingDamages = createPostAction({
  name: 'EDIT_ARRIVAL_ROOM_CHECK_EXISTING_DAMAGES',
  initialAction: (
    {bookingSetId, arrivalRoomCheckId, newExistingDamages},
    extraActions = []
  ) => ({
    payload: {
      bookingSetId,
      arrivalRoomCheckId,
      newExistingDamages,
      extraActions
    }
  })
});

export const editArrivalRoomCheckDescription = createPostAction({
  name: 'EDIT_ARRIVAL_ROOM_CHECK_DESCRIPTION',
  initialAction: (
    {bookingSetId, arrivalRoomCheckId, newDescription},
    extraActions = []
  ) => ({
    payload: {
      bookingSetId,
      arrivalRoomCheckId,
      newDescription,
      extraActions
    }
  }),
  formName: 'editArrivalRoomCheckDescriptionForm'
});

export const editDepartureRoomCheckDescription = createPostAction({
  name: 'EDIT_DEPARTURE_ROOM_CHECK_DESCRIPTION',
  initialAction: (
    {bookingSetId, departureRoomCheckId, newDescription},
    extraActions = []
  ) => ({
    payload: {
      bookingSetId,
      departureRoomCheckId,
      newDescription,
      extraActions
    }
  }),
  formName: 'editDepartureRoomCheckDescriptionForm'
});

export const editDepartureRoomCheckTotalDamageCharge = createPostAction({
  name: 'EDIT_DEPARTURE_ROOM_CHECK_TOTAL_DAMAGE_CHARGE',
  initialAction: (
    {bookingSetId, departureRoomCheckId, newTotalDamageCharge},
    extraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {
      bookingSetId,
      departureRoomCheckId,
      newTotalDamageCharge,
      extraActions,
      failureExtraActions
    }
  })
});

export const editDepartureRoomCheckChargeFromDeposit = createPostAction({
  name: 'EDIT_DEPARTURE_ROOM_CHECK_CHARGE_FROM_DEPOSIT',
  initialAction: (
    {bookingSetId, departureRoomCheckId, newChargeFromDeposit},
    extraActions = []
  ) => ({
    payload: {
      bookingSetId,
      departureRoomCheckId,
      newChargeFromDeposit,
      extraActions
    }
  })
});

export const editDepartureRoomCheckReleaseFullDeposit = createPostAction({
  name: 'EDIT_DEPARTURE_ROOM_CHECK_RELEASE_FULL_DEPOSIT',
  initialAction: (
    {bookingSetId, departureRoomCheckId, newReleaseFullDeposit},
    extraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {
      bookingSetId,
      departureRoomCheckId,
      newReleaseFullDeposit,
      extraActions,
      failureExtraActions
    }
  })
});

export const editDepartureRoomCheckStatus = createPostAction({
  name: 'EDIT_DEPARTURE_ROOM_CHECK_STATUS',
  initialAction: ({bookingSetId, departureRoomCheckId}, extraActions = []) => ({
    payload: {
      bookingSetId,
      departureRoomCheckId,
      extraActions
    }
  })
});

export const editDepositSummaryAmountToBeRetainedPaid = createPostAction({
  name: 'EDIT_DEPOSIT_SUMMARY_AMOUNT_TO_BE_RETAINED_PAID',
  initialAction: (
    {bookingSetId, newAmountToBeRetainedPaid},
    extraActions = []
  ) => ({
    payload: {
      bookingSetId,
      newAmountToBeRetainedPaid,
      extraActions
    }
  })
});

export const editArrivalRoomCheckStatus = createPostAction({
  name: 'EDIT_ARRIVAL_ROOM_CHECK_STATUS',
  initialAction: ({bookingSetId, arrivalRoomCheckId}, extraActions = []) => ({
    payload: {
      bookingSetId,
      arrivalRoomCheckId,
      extraActions
    }
  })
});

export const editDepositSummaryRefundToBeIssuedPaid = createPostAction({
  name: 'EDIT_DEPOSIT_SUMMARY_REFUND_TO_BE_ISSUED_PAID',
  initialAction: (
    {bookingSetId, newRefundToBeIssuedPaid},
    extraActions = []
  ) => ({
    payload: {
      bookingSetId,
      newRefundToBeIssuedPaid,
      extraActions
    }
  })
});

export const editDepositReleaseDueDate = createPostAction({
  name: 'EDIT_DEPOSIT_RELESE_DUE_DATE',
  initialAction: (
    {bookingSetId, newDepositReleaseDueDate},
    extraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {
      bookingSetId,
      newDepositReleaseDueDate,
      extraActions,
      failureExtraActions
    }
  })
});

export const editDepositSummaryDepositType = createPostAction({
  name: 'EDIT_DEPOSIT_TYPE',
  initialAction: ({bookingSetId, newDepositType}, extraActions = []) => ({
    payload: {
      bookingSetId,
      newDepositType,
      extraActions
    }
  })
});

export const verifyDepartureRoomCheckImageUpload = createPostAction({
  name: 'VERIFY_DEPARTURE_ROOM_CHECK_IMAGE_UPLOAD',
  initialAction: (
    {bookingSetId, imageIdentifier, departureRoomCheckId},
    extraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {
      bookingSetId,
      imageIdentifier,
      departureRoomCheckId,
      extraActions,
      failureExtraActions
    }
  })
});

export const removeDepartureRoomCheckImage = createPostAction({
  name: 'REMOVE_DEPARTURE_ROOM_CHECK_IMAGE',
  initialAction: (
    {bookingSetId, departureRoomCheckId, roomCheckImageId},
    extraActions = []
  ) => ({
    payload: {
      bookingSetId,
      departureRoomCheckId,
      roomCheckImageId,
      extraActions
    }
  })
});

export const removeDepartureRoomCheckInventory = createPostAction({
  name: 'REMOVE_DEPARTURE_ROOM_CHECK_INVENTORY',
  initialAction: (
    {bookingSetId, departureRoomCheckId, roomCheckInventoryId},
    extraActions = []
  ) => ({
    payload: {
      bookingSetId,
      departureRoomCheckId,
      roomCheckInventoryId,
      extraActions
    }
  })
});

export const removeDepartureRoomCheckMaintenance = createPostAction({
  name: 'REMOVE_DEPARTURE_ROOM_CHECK_MAINTENANCE',
  initialAction: (
    {bookingSetId, departureRoomCheckId, roomCheckMaintenanceId},
    extraActions = []
  ) => ({
    payload: {
      bookingSetId,
      departureRoomCheckId,
      roomCheckMaintenanceId,
      extraActions
    }
  })
});

export const verifyDepartureRoomCheckInventoryUpload = createPostAction({
  name: 'VERIFY_DEPARTURE_ROOM_CHECK_INVENTORY_UPLOAD',
  initialAction: (
    {bookingSetId, documentIdentifier, departureRoomCheckId},
    extraActions = []
  ) => ({
    payload: {
      bookingSetId,
      documentIdentifier,
      departureRoomCheckId,
      extraActions
    }
  })
});

export const verifyDepartureRoomCheckMaintenanceUpload = createPostAction({
  name: 'VERIFY_DEPARTURE_ROOM_CHECK_MAINTENANCE_UPLOAD',
  initialAction: (
    {bookingSetId, documentIdentifier, departureRoomCheckId},
    extraActions = []
  ) => ({
    payload: {
      bookingSetId,
      documentIdentifier,
      departureRoomCheckId,
      extraActions
    }
  })
});

export const ROOM_CHECK_STATUSES = {
  COMPLETED: 'Completed'
};

export const MODALS = {
  SELECT_BOOKING: 'selectBooking',
  BULK_INVOICE_VOID: 'bulkInvoiceVoid',
  ADD_ARRIVAL_ROOM_CHECK: 'addArrivalRoomCheck',
  ADD_DEPARTURE_ROOM_CHECK: 'addDepartureRoomCheck',
  ARRIVAL_ROOM_CHECK_DETAIL_MODAL: 'arrivalRoomCheckDetailModal',
  CONFIRM_ADD_DEPARTURE_ROOM_CHECK: 'confirmAddDepartureRoomCheckModal',
  DEPARTURE_ROOM_CHECK_DETAIL_MODAL: 'departureRoomCheckDetailModal',
  DEPOSIT_SUMMARY_DETAIL_MODAL: 'depositSummaryDetailModal'
};

//This delay is needed because the call to S3 is slower than the fetch we make again
export const REFETCH_ROOM_CHECK_DELAY = 2000;

const BOOKING_SET_STATUSES = {
  CANCELLED: 'Cancelled',
  UPCOMING: 'Upcoming',
  CHECKED_OUT: 'Checked out',
  DUE_IN: 'Due in',
  DUE_OUT: 'Due out',
  IN_HOUSE: 'In house',
  ONGOING: 'Ongoing',
  NO_SHOW: 'No show'
};

const BOOKING_SET_STATUS_GROUPS = {
  IN_THE_BUILDING: 'In the building',
  UPCOMING: 'Upcoming',
  CANCELLED: 'Cancelled',
  CHECKED_OUT: 'Checked out'
};

const BOOKING_SET_STATUS_GROUP_TO_BOOKING_STATUSES = {
  [BOOKING_SET_STATUS_GROUPS.UPCOMING]: [
    BOOKING_SET_STATUSES.UPCOMING,
    BOOKING_SET_STATUSES.DUE_IN,
    BOOKING_SET_STATUSES.NO_SHOW
  ],
  [BOOKING_SET_STATUS_GROUPS.IN_THE_BUILDING]: [
    BOOKING_SET_STATUSES.IN_HOUSE,
    BOOKING_SET_STATUSES.ONGOING,
    BOOKING_SET_STATUSES.DUE_OUT
  ],
  [BOOKING_SET_STATUS_GROUPS.CANCELLED]: [BOOKING_SET_STATUSES.CANCELLED],
  [BOOKING_SET_STATUS_GROUPS.CHECKED_OUT]: [BOOKING_SET_STATUSES.CHECKED_OUT]
};

export const getBookingSetStatusGroups = (): Array<string> => {
  return Object.keys(BOOKING_SET_STATUS_GROUPS).map(
    key => BOOKING_SET_STATUS_GROUPS[key]
  );
};

export const getBookingSetStatusesForStatusGroup = (
  bookingSetStatusGroup: string
): Array<string> => {
  return _.get(
    BOOKING_SET_STATUS_GROUP_TO_BOOKING_STATUSES,
    bookingSetStatusGroup,
    []
  );
};

const initialState = {};

const fetchVoidableInvoicesForBookingSetReducer = createFetchReducers(
  fetchVoidableInvoicesForBookingSet,
  (state, action) => ({
    ...state,
    [action.parent.stateKey]: {
      data: action.payload.map(attachUuidKey),
      isLoading: false
    }
  })
);

const fetchReducers = createReducer(initialState, {
  ...createFetchReducersWithPagination(fetchBookingSets),
  ...createFetchReducersWithPagination(fetchBookingSetsByTenant),
  ...createFetchReducers(fetchBookingSet),
  ...createFetchReducers(fetchBookingSetsFinanceData),
  ...createFetchReducers(fetchBookingSetDepositFinanceData),
  ...fetchVoidableInvoicesForBookingSetReducer,
  ...createFetchReducers(fetchArrivalRoomChecks),
  ...createFetchReducers(fetchDepartureRoomCheck),
  ...createFetchReducers(fetchDepartureRoomChecks),
  ...createFetchReducers(fetchRoomOptionsForAddArrivalRoomCheck),
  ...createFetchReducers(fetchRoomOptionsForAddDepartureRoomCheck),
  ...createFetchReducers(fetchImageUrlAndFileName),
  ...createFetchReducers(fetchDocumentFileName),
  ...createFetchReducers(fetchDepositSummary),
  ...createFetchReducers(fetchDepositTypes),
  ...createFetchReducers(fetchDepositSummaryStatuses),
  ...createFetchReducers(fetchRoomChecksTimeline)
});

const reducer = (state = initialState, action) => {
  const newState = fetchReducers(state, action);

  switch (action.type) {
    default:
      return newState;
  }
};

export default reducer;
export {default as bookingSetsSaga} from './saga';
